<template>
  <div class="sidebar-wrapper">
    <div class="sidebar-logo" v-if="companyData.logoUrl">
      <img :src="companyData.logoUrl" alt="Logo" />
    </div>
    <div class="sidebar-links-wrapper">
      <div
        v-for="(page, index) in pages"
        :key="index"
        @click="activePage = index"
        class="link-item"
        :class="getPageClass(index)"
        v-key="page.ref"
      >
        <router-link class="link-main" :to="page.path" v-if="page.path">
          <span class="link-icon mr1">
            <i :class="page.icon" />
          </span>
          <span class="link-name" v-text="t(page.name)" />
        </router-link>
        <div class="link-main" v-else>
          <span class="link-icon mr1">
            <i :class="page.icon" />
          </span>
          <span class="link-name" v-text="t(page.name)" />
        </div>
        <div class="link-hover" v-text="t(page.name)" />
        <div class="sub-links" v-if="page.subMenu.length > 0 && activePage === index">
          <router-link
            :to="sub.path"
            class="sub-link"
            :class="activeSubPage === i ? 'active-sub-page' : ''"
            v-for="(sub, i) in page.subMenu"
            :key="i"
            v-key="sub.ref"
            v-text="t(sub.name)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import pages from '@/data/pages.json'
  import { useDataStore } from '@/pinia'
  import { UseI18n } from '@/utils-ts'
  import { computed, onMounted, ref } from 'vue'
  import { useRoute } from 'vue-router'

  const dataStore = useDataStore()
  const route = useRoute()
  const { t } = UseI18n()

  const activePage = ref<number>(0)

  const companyData = computed(() => dataStore.companyInfo)

  const activeSubPage = computed(() => {
    const path = route.path
    const subPage = pages[activePage.value]?.subMenu.findIndex(sub => sub.path === path)
    return subPage
  })

  onMounted(() => {
    const path = route.path
    const page = pages.findIndex(page => page.path === path)
    if (page !== -1) {
      activePage.value = page
    } else {
      activePage.value = (route.meta.index as number) || -1
    }
  })

  const getPageClass = (index: number) => {
    if (!pages[index]) return {}
    const hasSub = pages[index].subMenu.length > 0
    return {
      'active-page': activePage.value === index,
      'has-sub': hasSub,
    }
  }
</script>

<style lang="scss" scoped>
  .sidebar-wrapper {
    width: 100%;
    height: 100%;
    background-color: $sidebarColor;
    box-shadow: 3px 0px 5px 0px rgba(0, 0, 0, 0.2);
    overflow-y: auto;
    position: relative;

    @include scroll-bar($width: 2px);
    a {
      text-decoration: none;
      color: $brandColor;
      display: flex;
      width: 100%;
    }
    .sidebar-logo {
      width: 100%;
      @include flexible($justify: center, $align: center);
      padding: 2rem 0;
      img {
        max-width: 90%;
      }
    }
    .sidebar-links-wrapper {
      @include flexible($direction: column, $justify: center);
      width: 100%;
      .link-item {
        @include flexible($direction: column, $justify: center);
        width: 100%;
        cursor: pointer;
        transition: 0.2s ease;
        color: $brandColor;
        position: relative;
        .link-hover {
          display: none;
          position: absolute;
          bottom: 100%;
          right: 0;
          background-color: rgba(0, 0, 0, 0.7);
          z-index: 100;
          color: $white;
          font-size: 0.625rem;
          padding: 0.25rem 0.5rem;
        }
        .link-main {
          @include flexible($justify: flex-start, $align: center);
          margin: 0.5rem 1rem;
          width: 100%;
          .link-icon {
            @include flexible($justify: center, $align: center);
            height: 1.5rem;
            width: 1.5rem;
          }
          .link-name {
            width: calc(100% - 4rem);
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            color: $primary-text-color;
            font-weight: 600;
          }
        }
        .sub-links {
          width: 100%;
          background-color: $white;
          color: $primary-text-color;
          padding: 0.5rem 2rem;
          font-weight: 600;
          .sub-link {
            color: $primary-text-color;
            padding: 0.25rem 0.5rem;
            border-left: 3px solid transparent;
            &.active-sub-page {
              border-left: 3px solid $brandColor;
              background-color: #e5e5e5;
              border-radius: 0 5px 5px 0;
            }
          }
        }
        @mixin activeHover {
          background-color: rgba($brandColorRgb, 0.7);
          .link-icon {
            color: $white;
          }
          .link-name {
            color: $white;
          }
        }

        &.active-page {
          @include activeHover;
        }
        &:hover {
          @include activeHover;
          .link-hover {
            display: block;
          }
        }
      }
    }
  }
</style>
